import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';

import { ApiError } from '@/shared/api/client';
import { brokerSelfSignup, type BrokerSelfSignupReceipt } from '@/shared/api/resources';
import { ErrorCode } from '@/shared/api/types';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';

import { PublicFrame } from './PublicFrame';
import { usePortalConfig } from './usePortalConfig';

/**
 * Versao do aviso de privacidade mostrado aqui. E a mesma que o servidor grava em cada
 * pedido (BrokerSelfSignupPrivacyNoticeVersion): trocar o texto pede trocar as duas.
 */
export const VERSAO_DO_AVISO_DE_PRIVACIDADE = '2026-10-23';

/** Mensagem da recusa do inicio, pelo codigo e pelo motivo, e nao pelo texto. */
function mensagemDaRecusa(erro: unknown): string {
  if (!(erro instanceof ApiError)) {
    return 'Nao foi possivel iniciar o cadastro. Tente novamente.';
  }
  const motivo = erro.details?.['reason'];
  switch (erro.code) {
    case ErrorCode.Conflict:
      if (motivo === 'corretora_ja_cadastrada') {
        // Texto do OnPoint (USU-108).
        return 'Erro! A corretora ja encontra-se cadastrada no sistema!';
      }
      if (motivo === 'cadastro_em_analise') {
        return 'Ja existe um cadastro desta corretora em analise pela seguradora.';
      }
      return erro.message;
    case ErrorCode.NotFound:
      return 'O autocadastro de corretora nao esta disponivel para esta empresa.';
    case ErrorCode.RateLimited:
      return 'Muitos pedidos em pouco tempo. Aguarde e tente de novo mais tarde.';
    case ErrorCode.ValidationFailed:
      return erro.message;
    // Triagem da SUSEP (ADR 0095): a mensagem do servidor diz por que a corretora nao
    // segue, ou que a SUSEP nao respondeu.
    case ErrorCode.BrokerSusepNotApt:
    case ErrorCode.BrokerSusepUnavailable:
      return erro.message;
    default:
      return 'Nao foi possivel iniciar o cadastro. Tente novamente.';
  }
}

/**
 * Inicio do autocadastro da corretora (o /iniciar-cadastro do OnPoint, ACE-036 a
 * ACE-040), com a marca da empresa.
 *
 * Alem do CNPJ, pede o e-mail que recebe o link do cadastro e a ciencia do aviso de
 * privacidade: o assistente so abre pelo link, o que confirma o e-mail. A protecao
 * contra abuso e do servidor (teto por origem e por CNPJ).
 */
export function BrokerSelfSignupStartPage() {
  const { tenantSlug } = useParams();
  const { portal, carregando } = usePortalConfig(tenantSlug);

  const [cnpj, setCnpj] = useState('');
  const [email, setEmail] = useState('');
  const [ciente, setCiente] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [recibo, setRecibo] = useState<BrokerSelfSignupReceipt | null>(null);

  const reconhecida = portal?.resolved ? portal : null;
  const slug = reconhecida?.tenant_slug ?? tenantSlug ?? '';
  const voltar = slug ? `/entrar/${encodeURIComponent(slug)}` : '/entrar';
  const aberto = reconhecida?.enable_registration_broker === true;
  const empresa = reconhecida?.display_name || 'a seguradora';

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    void iniciar();
  }

  async function iniciar() {
    setErro(null);
    if (!ciente) {
      setErro('Leia o aviso de privacidade e confirme a ciencia para continuar.');
      return;
    }
    setEnviando(true);
    try {
      setRecibo(
        await brokerSelfSignup.start({
          ...(slug ? { tenant_slug: slug } : {}),
          ...(typeof window !== 'undefined' && !slug
            ? { domain: window.location.host }
            : {}),
          document: cnpj,
          contact_email: email,
          privacy_acknowledged: ciente,
        }),
      );
    } catch (causa) {
      setErro(mensagemDaRecusa(causa));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <PublicFrame portal={portal}>
      <h1 className="text-xl font-semibold text-ink-900">Cadastro de corretora</h1>
      <p className="mt-1 mb-7 text-sm text-ink-500">Digite seu CNPJ</p>

      {!carregando && !aberto ? (
        <div className="flex flex-col gap-4">
          <Alert tone="info" title="Cadastro indisponivel">
            O autocadastro de corretora nao esta disponivel para esta empresa. Fale com
            a seguradora para cadastrar a sua corretora.
          </Alert>
          <Link
            to={voltar}
            className="text-center text-sm font-medium text-brand-600 hover:underline"
          >
            Voltar para o acesso
          </Link>
        </div>
      ) : recibo ? (
        <div className="flex flex-col gap-4">
          <Alert
            tone={recibo.email_delivery_available ? 'success' : 'warning'}
            title={
              recibo.email_delivery_available
                ? 'Confira o seu e-mail'
                : 'Pedido registrado'
            }
          >
            {recibo.message}
            {recibo.email_delivery_available
              ? ' Abra o link para preencher os dados da corretora.'
              : null}
          </Alert>
          <Link
            to={voltar}
            className="text-center text-sm font-medium text-brand-600 hover:underline"
          >
            Voltar para o acesso
          </Link>
        </div>
      ) : (
        <form onSubmit={enviar} noValidate className="flex flex-col gap-4">
          {erro ? <Alert tone="danger">{erro}</Alert> : null}

          <Field
            label="CNPJ"
            name="document"
            inputMode="numeric"
            required
            value={cnpj}
            onChange={(evento) => setCnpj(evento.target.value)}
            placeholder="Digite seu CNPJ"
          />
          <Field
            label="E-mail para receber o link do cadastro"
            type="email"
            name="contact_email"
            autoComplete="email"
            required
            value={email}
            onChange={(evento) => setEmail(evento.target.value)}
            placeholder="Digite seu e-mail"
          />

          <div
            className="rounded-md border border-line bg-ink-50 p-3 text-2xs leading-relaxed text-ink-600"
            data-testid="aviso-de-privacidade"
          >
            Os dados informados no cadastro (CNPJ, e-mail de contato, dados da
            corretora, dados bancarios e dados das pessoas indicadas) sao usados por{' '}
            {empresa} para analisar o pedido de cadastro da corretora, a pedido dela
            (Lei 13.709/2018, art. 7o, V). O pedido nao confirmado e apagado quando o
            link vence. Na decisao, o rascunho e apagado: os dados aprovados passam ao
            cadastro da corretora e aos convites de acesso. Versao do aviso:{' '}
            {VERSAO_DO_AVISO_DE_PRIVACIDADE}.
          </div>
          <label className="flex items-start gap-2 text-xs text-ink-700">
            <input
              type="checkbox"
              name="privacy_acknowledged"
              checked={ciente}
              onChange={(evento) => setCiente(evento.target.checked)}
              className="mt-0.5"
            />
            Li o aviso de privacidade e estou ciente do uso dos dados.
          </label>

          <div className="mt-2 flex gap-3">
            <Link
              to={voltar}
              className="inline-flex h-12 flex-1 items-center justify-center rounded-md border border-line-strong text-sm font-medium text-ink-700 hover:bg-ink-50"
            >
              Voltar
            </Link>
            <Button type="submit" size="lg" loading={enviando} className="flex-[2]">
              Iniciar cadastro
            </Button>
          </div>
        </form>
      )}
    </PublicFrame>
  );
}
